import { useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/Button";
import { Field, Input, PasswordInput } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { AuthAlreadyRedirect, AuthCtaButton, AuthLayout } from "@/components/layout/AuthLayout";
import { useI18n } from "@/hooks/useI18n";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/providers/AuthProvider";
import { signupErrorMessage } from "./authErrors";

/** Set by `LoginPage` when a Google sign-in found no linked coach account. */
export interface SignupPrefill {
    email?: string;
    fullName?: string;
    googleIdToken?: string;
}

/** Cheap client-side guard — the API validates the same shape with Zod. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SignupPage() {
    const { register } = useAuth();
    const { t } = useI18n();
    const navigate = useNavigate();
    const location = useLocation();
    const prefill = (location.state ?? {}) as SignupPrefill;
    // Token cukup dibawa dari LoginPage — signup TIDAK pernah popup Google ulang.
    // Kalau token basi, lempar balik ke /login biar 1-klik Google di sana.
    const googleIdToken = prefill.googleIdToken ?? undefined;
    const googleLinked = Boolean(googleIdToken);

    const [fullName, setFullName] = useState(prefill.fullName ?? "");
    const [dial, setDial] = useState("+62");
    const [phoneNational, setPhoneNational] = useState("");
    const [email, setEmail] = useState(prefill.email ?? "");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);

        const trimmedName = fullName.trim();
        const trimmedEmail = email.trim().toLowerCase();
        const nationalDigits = phoneNational.replace(/\D/g, "").replace(/^0+/, "");
        const trimmedPhone = `${dial}${nationalDigits}`;

        if (trimmedName.length < 2) {
            setError(t("auth.signup.fullNameInvalid"));
            return;
        }
        if (nationalDigits.length < 7 || nationalDigits.length > 14) {
            setError(t("auth.signup.phoneInvalid"));
            return;
        }
        if (!EMAIL_PATTERN.test(trimmedEmail)) {
            setError(t("auth.signup.emailInvalid"));
            return;
        }
        if (password.length < 8) {
            setError(t("auth.signup.passwordTooShort"));
            return;
        }
        if (password !== confirmPassword) {
            setError(t("auth.signup.passwordMismatch"));
            return;
        }
        if (googleLinked && prefill.email && trimmedEmail !== prefill.email.trim().toLowerCase()) {
            setError(t("auth.signup.googleMismatch"));
            return;
        }

        setSubmitting(true);
        try {
            await register({
                fullName: trimmedName,
                phone: trimmedPhone,
                email: trimmedEmail,
                password,
                ...(googleIdToken ? { googleIdToken } : {}),
            });
            // OTP-dulu: daftar TIDAK PERNAH langsung ke /plan — termasuk
            // jalur Google. Backend tidak menerbitkan sesi sebelum OTP
            // lolos; walau kirim email sempat gagal, tetap ke halaman OTP
            // karena di sana tersedia tombol resend.
            navigate("/verify-email", {
                replace: true,
                state: { email: trimmedEmail, justRegistered: true },
            });
        } catch (caught) {
            // Token Google dari LoginPage basi — jangan popup di sini,
            // balik ke /login biar user tinggal 1-klik Google lagi.
            const stale =
                googleIdToken &&
                caught instanceof ApiError &&
                caught.status === 401 &&
                /invalid google/i.test(caught.message);
            if (stale) {
                navigate("/login", { replace: true });
                return;
            }
            setError(signupErrorMessage(caught, t));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <AuthAlreadyRedirect />
            <AuthLayout
                subtitle={t("auth.signup.subtitle")}
                cardClass="max-w-xl"
                gridClass="lg:grid-cols-[0.9fr_1.1fr]"
                logoLink="/login"
                heroCta={
                    <>
                        <p className="app-text-primary text-lg font-semibold">
                            {t("auth.signup.haveAccount")}
                        </p>
                        <AuthCtaButton to="/login">{t("auth.signup.backToSignIn")}</AuthCtaButton>
                    </>
                }
            >
                <form
                    onSubmit={handleSubmit}
                    autoComplete="off"
                    className="app-content-card rounded-2xl border p-6 shadow-md"
                >
                    <div className="flex flex-col gap-4">
                        {googleLinked && (
                            <Alert tone="info">{t("auth.signup.googleLinkedNote")}</Alert>
                        )}

                        <Field label={t("auth.signup.fullName")} required>
                            <Input
                                value={fullName}
                                onChange={(event) => setFullName(event.target.value)}
                                placeholder={t("auth.signup.fullNamePlaceholder")}
                                autoComplete="name"
                                autoFocus
                                required
                            />
                        </Field>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field label={t("auth.signup.phone")} required>
                                <PhoneInput
                                    national={phoneNational}
                                    dial={dial}
                                    onNationalChange={setPhoneNational}
                                    onDialChange={setDial}
                                    placeholder={t("auth.signup.phonePlaceholder")}
                                    required
                                />
                            </Field>

                            <Field label={t("auth.signup.email")} required>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                    placeholder={t("auth.signup.emailPlaceholder")}
                                    autoComplete="email"
                                    readOnly={googleLinked}
                                    required
                                />
                            </Field>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field label={t("auth.signup.password")} hint={t("auth.signup.passwordHint")} required>
                                <PasswordInput
                                    value={password}
                                    onChange={(event) => setPassword(event.target.value)}
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                    required
                                />
                            </Field>

                            <Field label={t("auth.signup.confirmPassword")} required>
                                <PasswordInput
                                    value={confirmPassword}
                                    onChange={(event) => setConfirmPassword(event.target.value)}
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                    required
                                />
                            </Field>
                        </div>

                        {error && <Alert tone="error">{error}</Alert>}

                        <Button type="submit" size="lg" loading={submitting} fullWidth>
                            {t("auth.signup.submit")}
                        </Button>
                    </div>
                </form>
            </AuthLayout>
        </>
    );
}
